/**
 * Drop-in replacement for @auth0/auth0-react using Keycloak.
 * Exports useAuth0, Auth0Provider, and Auth0ContextInterface with the same
 * API surface so that all consumer files remain unchanged.
 */
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import Keycloak from 'keycloak-js';

const KC_URL    = process.env.REACT_APP_KEYCLOAK_URL    || 'https://idp.evotechnica.com';
const KC_REALM  = process.env.REACT_APP_KEYCLOAK_REALM  || 'botala';
const KC_CLIENT = process.env.REACT_APP_KEYCLOAK_CLIENT || 'botala-web';

export interface Auth0User {
  sub: string;
  email?: string;
  name?: string;
  picture?: string;
  email_verified?: boolean;
  nickname?: string;
  given_name?: string;
  family_name?: string;
  [key: string]: unknown;
}

export interface Auth0ContextInterface {
  isLoading: boolean;
  isAuthenticated: boolean;
  user?: Auth0User;
  error?: Error;
  getAccessTokenSilently: () => Promise<string>;
  loginWithRedirect: (opts?: { appState?: { returnTo?: string }; authorizationParams?: Record<string, string> }) => Promise<void>;
  logout: (opts?: { logoutParams?: { returnTo?: string } }) => void;
}

function mapUser(parsed: Record<string, unknown>): Auth0User {
  return {
    sub:            String(parsed.sub ?? ''),
    email:          parsed.email as string | undefined,
    email_verified: Boolean(parsed.email_verified),
    name:           (parsed.name || parsed.preferred_username) as string | undefined,
    nickname:       parsed.preferred_username as string | undefined,
    given_name:     parsed.given_name as string | undefined,
    family_name:    parsed.family_name as string | undefined,
    picture:        parsed.picture as string | undefined,
    ...parsed,
  };
}

const Auth0Context = createContext<Auth0ContextInterface | null>(null);

interface Auth0ProviderProps {
  children: ReactNode;
}

export const Auth0Provider: React.FC<Auth0ProviderProps> = ({ children }) => {
  const kcRef = useRef<Keycloak | null>(null);
  const [state, setState] = useState<Omit<Auth0ContextInterface, 'getAccessTokenSilently' | 'loginWithRedirect' | 'logout'>>({
    isLoading:       true,
    isAuthenticated: false,
    user:            undefined,
    error:           undefined,
  });

  useEffect(() => {
    const kc = new Keycloak({ url: KC_URL, realm: KC_REALM, clientId: KC_CLIENT });
    kcRef.current = kc;

    kc.init({
      onLoad:          'check-sso',
      silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
      pkceMethod:      'S256',
      checkLoginIframe: false,
    }).then(authenticated => {
      if (authenticated) {
        kc.onTokenExpired = () => kc.updateToken(30).catch(() => kc.logout());
      }
      setState({
        isLoading:       false,
        isAuthenticated: authenticated,
        user:            authenticated ? mapUser(kc.tokenParsed as Record<string, unknown>) : undefined,
        error:           undefined,
      });
    }).catch((err: Error) => {
      setState({ isLoading: false, isAuthenticated: false, user: undefined, error: err });
    });
  }, []);

  const ctx: Auth0ContextInterface = {
    ...state,
    getAccessTokenSilently: async () => {
      const kc = kcRef.current;
      if (!kc) throw new Error('Keycloak not initialized');
      await kc.updateToken(10).catch(() => { kc.login(); });
      return kc.token ?? '';
    },
    loginWithRedirect: async (opts) => {
      const kc = kcRef.current;
      if (!kc) return;
      const returnTo = opts?.appState?.returnTo || window.location.pathname;
      sessionStorage.setItem('kc_return_to', returnTo);
      kc.login({ redirectUri: window.location.origin });
    },
    logout: (opts) => {
      const kc = kcRef.current;
      if (!kc) return;
      kc.logout({ redirectUri: opts?.logoutParams?.returnTo || window.location.origin });
    },
  };

  return React.createElement(Auth0Context.Provider, { value: ctx }, children);
};

export function useAuth0(): Auth0ContextInterface {
  const ctx = useContext(Auth0Context);
  if (!ctx) throw new Error('useAuth0 must be used within Auth0Provider');
  return ctx;
}
